import { inject, Injectable } from '@angular/core';
import { RouteService, SqliteService } from '@app/services';
import { RouteModel, RouteStoreModel, UserModel } from '@app/models';
import { UserRepository } from './user.repository';
import { StoreRepository } from './store.repository';
import { ProductRepository } from './product.repository';
import { PriceListRepository } from './price-list.repository';
import { PreorderedProductRepository } from './preordered-product.repository';
import { PaymentMethodRepository } from './payment-method.repository';
import { NoDeliveryReasonRepository } from './no-delivery-reason.repository';
import { ReturnReasonRepository } from './return-reason.repository';
import { PowerOfOneRepository } from './power-of-one.repository';

@Injectable({
    providedIn: 'root',
})
export class RouteRepository
{
    private readonly keys = {
        tableName: 'routes',
        activeRoutesTableName: 'active_routes',
        linkTableName: 'route_store_user',
    };
    private powerOfOneRepository = inject(PowerOfOneRepository);
    private userRepository = inject(UserRepository);
    private sqliteService = inject(SqliteService);
    private routeService = inject(RouteService);
    private storeRepository = inject(StoreRepository);
    private productRepository = inject(ProductRepository);
    private priceListRepository = inject(PriceListRepository);
    private preorderedProductRepository = inject(PreorderedProductRepository);
    private paymentMethodRepository = inject(PaymentMethodRepository);
    private noDeliveryReasonRepository = inject(NoDeliveryReasonRepository);
    private returnReasonRepository = inject(ReturnReasonRepository);

    async downloadTodayRoutes(): Promise<RouteModel[]>
    {
        const routes = await this.routeService.getTodayRoutes();

        // if (routes.length > 0)
        // {
        //     await this.saveTodayRoutes(routes);
        // }

        return routes;
    }

    async getTodayRoutes(): Promise<RouteModel[]>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return [];
        }

        const sql = `
            SELECT DISTINCT ${this.keys.tableName}.*
            FROM ${this.keys.tableName}
                     JOIN ${this.keys.linkTableName} rsu ON ${this.keys.tableName}.external_id = rsu.route_external_id
            WHERE rsu.user_external_id = ? AND DATE (rsu.created_at) = DATE ('now');
        `;
        const data = await this.sqliteService.get(sql, [user.external_id]) ?? [];
        const routes = data as RouteModel[];

        for (const route of routes)
        {
            route.stores = await this.storeRepository.getRouteStores(route);
        }

        return routes;
    }

    async startRoute(route: RouteModel): Promise<boolean>
    {
        const routeData = await this.routeService.getRouteData(route.id);
        const result = await this.routeService.startRoute(route.id);

        if (!result)
        {
            return false;
        }

        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return false;
        }

        await this.saveTodayRoutes([routeData.route]);

        await this.productRepository.saveProducts(routeData.products);
        await this.priceListRepository.savePriceLists(routeData.price_lists);
        await this.preorderedProductRepository.savePreorderedProducts(routeData.preordered_items);
        await this.paymentMethodRepository.savePaymentMethods(routeData.payment_methods);
        await this.noDeliveryReasonRepository.saveNoDeliveryReasons(routeData.no_delivery_reasons);
        await this.returnReasonRepository.saveReturnReasons(routeData.return_reasons);
        await this.powerOfOneRepository.savePowerOfOneStores(routeData.power_of_one_stores);

        const columns = [
            'user_id',
            'route_id',
            'start_date',
        ];
        const valuePlaceholders = this.sqliteService.getValuePlaceholder(1, columns.length);
        const sql = `
            INSERT INTO ${this.keys.activeRoutesTableName} (${columns.join(', ')})
            VALUES ${valuePlaceholders};
        `;
        await this.sqliteService.save(sql, [
            user.id,
            route.id,
            new Date().toISOString(),
        ]);

        return true;
    }

    async getOverdueRoute(): Promise<RouteModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        const sql = `
            SELECT ${this.keys.tableName}.*
            FROM ${this.keys.tableName}
                     JOIN ${this.keys.activeRoutesTableName} ON ${this.keys.tableName}.id = ${this.keys.activeRoutesTableName}.route_id
            WHERE ${this.keys.activeRoutesTableName}.user_id = ?
              AND DATE (${this.keys.activeRoutesTableName}.start_date)
                < DATE ('now')
              AND ${this.keys.activeRoutesTableName}.end_date IS NULL LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [user.id]);

        if (data === null)
        {
            return null;
        }

        const route = data[0] as RouteModel;
        route.stores = await this.storeRepository.getRouteStores(route);

        return route;
    }

    async updateRouteEndDate(route: RouteModel, endDate: string | null): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const columns = [
            'end_date',
        ];
        const setClause = columns.map((col) => `${col} = ?`).join(', ');
        const sql = `
            UPDATE ${this.keys.activeRoutesTableName}
            SET ${setClause}
            WHERE route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.save(sql, [
            endDate,
            route.id,
            user.id,
        ]);
    }

    async finishRoute(route: RouteModel): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const columns = [
            'end_date',
        ];
        const setClause = columns.map((col) => `${col} = ?`).join(', ');
        const sql = `
            UPDATE ${this.keys.activeRoutesTableName}
            SET ${setClause}
            WHERE route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.save(sql, [
            new Date().toISOString(),
            route.id,
            user.id,
        ]);
    }

    async clearUserRoute(route: RouteModel): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        let sql = `
            DELETE
            FROM ${this.keys.activeRoutesTableName}
            WHERE user_id = ?
              AND route_id = ?;
        `;
        await this.sqliteService.delete(sql, [user.id, route.id]);

        sql = `
            DELETE
            FROM ${this.keys.linkTableName}
            WHERE route_external_id = ?
              AND user_external_id = ?;
        `;
        await this.sqliteService.delete(sql, [route.external_id, user.external_id]);

        sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE id = ?;
        `;
        await this.sqliteService.delete(sql, [route.id]);
    }

    async getActiveRoute(): Promise<RouteModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        const sql = `
            SELECT ${this.keys.tableName}.*
            FROM ${this.keys.tableName}
                     JOIN ${this.keys.activeRoutesTableName} ON ${this.keys.tableName}.id = ${this.keys.activeRoutesTableName}.route_id
            WHERE ${this.keys.activeRoutesTableName}.user_id = ?
              AND DATE (${this.keys.activeRoutesTableName}.start_date) = DATE ('now')
              AND ${this.keys.activeRoutesTableName}.end_date IS NULL LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [user.id]);

        if (data === null)
        {
            return null;
        }

        const route = data[0] as RouteModel;
        route.stores = await this.storeRepository.getRouteStores(route);

        return route;
    }

    private async saveTodayRoutes(routes: RouteModel[]): Promise<void>
    {
        const columns = [
            'id',
            'external_id',
            'name',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < routes.length; i += batchSize)
        {
            const chunk = routes.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(id) DO
                UPDATE
                    SET
                        external_id = excluded.external_id,
                    name = excluded.name;
            `;
            const values = chunk.flatMap((r) => [r.id, r.external_id, r.name]);
            await this.sqliteService.save(sql, values);
        }

        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        for (const route of routes)
        {
            if (route.stores && route.stores.length > 0)
            {
                await this.storeRepository.saveStores(route.stores);
                await this.saveRouteStoreUserLink(route, route.stores, user);
            }
        }
    }

    private async saveRouteStoreUserLink(route: RouteModel, stores: RouteStoreModel[], user: UserModel): Promise<void>
    {
        const columns = [
            'route_external_id',
            'store_external_id',
            'user_external_id',
            'sort_order',
            'created_at',
        ];
        const batchSize = Math.floor(3000 / columns.length);

        for (let i = 0; i < stores.length; i += batchSize)
        {
            const chunk = stores.slice(i, i + batchSize);
            const valuePlaceholders = this.sqliteService.getValuePlaceholder(chunk.length, columns.length);
            const sql = `
                INSERT INTO ${this.keys.linkTableName} (${columns.join(', ')})
                VALUES ${valuePlaceholders} ON CONFLICT(route_external_id, store_external_id) DO
                UPDATE
                    SET
                        user_external_id = excluded.user_external_id,
                    created_at = excluded.created_at,
                    sort_order = excluded.sort_order;
            `;
            const values = chunk.flatMap((r) => [
                route.external_id,
                r.external_id,
                user.external_id,
                r.sort_order ?? 0,
                new Date().toISOString(),
            ]);
            await this.sqliteService.save(sql, values);
        }
    }
}
